import { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';

import DragDropDialog from './dialogs/DragDropDialog';
import { FHButton, FHInputField } from '.';
import { getThemeColor } from '../../utils/common';
import { FORTA_ERROR, FORTA_WHITE } from '../../utils/colors';
import { Themes } from '../../constants';
import PdfIcon from '../../assets/images/pdf-icon.svg';
import DocumentUploadIcon from '../../assets/images/document-upload.svg';

import type { DropzoneProps, FileRejection } from 'react-dropzone';

type DropzoneSelectedFile = File & {
  preview?: string;
  noPreviewFile?: boolean;
};

interface FHDropzoneProps extends DropzoneProps {
  colorTheme: Themes;
  show: boolean;
  fileNameRegEx?: string;
  noPreviewFiles?: string[];
  title?: string;
  subTitle?: string;
  selectButtontext?: string;
  validateFilename?: boolean;
  browseButtonTestId?: string;
  uploadButtonTestId?: string;
  onClose: () => void;
  onSelect: (files: any) => void;
}

const FHDropzone = ({
  show,
  colorTheme = Themes.Primary,
  maxFiles = 1,
  maxSize = 10, // In MB
  accept,
  fileNameRegEx = '^[a-zA-Z0-9_., -]*$',
  noPreviewFiles = [],
  title = 'Upload Document',
  subTitle = 'Subtitle',
  selectButtontext = 'Select Visual',
  validateFilename = true,
  browseButtonTestId = 'browseButton',
  uploadButtonTestId = 'uploadButton',
  onSelect,
  onClose,
}: FHDropzoneProps) => {
  const [selectedFiles, setSelectedFiles] = useState<DropzoneSelectedFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const handleDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (
      fileRejections.some(({ errors }) =>
        errors.some(({ code }) => code === 'too-many-files'),
      )
    ) {
      setSelectedFiles([]);
      setErrors([`Maximum number of files allowed: ${maxFiles}`]);
      return;
    }

    if (acceptedFiles.length === 0) {
      if (
        fileRejections.some(({ errors }) =>
          errors.some(({ code }) => code === 'file-too-large'),
        )
      ) {
        setErrors([`Maximum file size allowed is ${maxSize} MB`]);
      }
      return;
    }

    const files = acceptedFiles.map((file: File) => {
      const type = file.type.split('/')[1];
      if (noPreviewFiles.includes(`.${type}`)) {
        return Object.assign(file, {
          noPreviewFile: true,
        });
      } else {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      }
    });
    setSelectedFiles(files);
    const errorList = [];
    const regex = new RegExp(fileNameRegEx);

    if (validateFilename && !regex.test(files[0].name)) {
      errorList.push('File name should only contain Numbers, Alphabets and -,_ only');
    }

    setErrors(errorList);
  }, [fileNameRegEx, maxFiles, maxSize, noPreviewFiles, validateFilename]);

  const { getRootProps, getInputProps, open } = useDropzone({
    maxFiles,
    maxSize: maxSize * 1024 * 1024,
    accept,
    noPaste: true,
    onDrop: handleDrop,
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      selectedFiles.forEach((file: any) => URL.revokeObjectURL(file.preview));
    };
  }, []);

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
    setErrors([]);
  };

  const handleSelect = () => {
    onSelect(selectedFiles);
    handleClose();
  };

  return (
    <DragDropDialog
      open={show}
      onClose={handleClose}
      colorTheme={colorTheme}
      title={title}
      subTitle={subTitle}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          width: '100%',
          height: 300,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: getThemeColor(colorTheme).light3,
          borderWidth: '1px',
          borderStyle: 'dashed',
          borderColor: getThemeColor(colorTheme).primary,
          borderRadius: '10px',
          p: 2,
          '& ::-webkit-scrollbar-track': {
            background: FORTA_WHITE,
          },

          '& ::-webkit-scrollbar-thumb': {
            background: getThemeColor(colorTheme).primary,
            borderColor: FORTA_WHITE,
          },
        }}
        textAlign="center"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} id="dropzone_input" />
        {selectedFiles.length > 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              overflowY: 'auto',
              gap: 2,
            }}
          >
            {selectedFiles.map((file: any) => {
              const isPdf = file.type === 'application/pdf';
              const isNoPreview = file.noPreviewFile;

              const getFileSize = (size: number) =>
                size < 1024 * 1024
                  ? `${(size / 1024).toFixed(2)} KB`
                  : `${(size / (1024 * 1024)).toFixed(2)} MB`;

              return isNoPreview ? (
                isPdf ? (
                  <Box
                    sx={{
                      width: '180px',
                      height: '180px',
                      padding: '8px',
                      backgroundColor: FORTA_WHITE,
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mb: 2,
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <img src={PdfIcon} alt="pdf-icon" width={60} height={80} />
                    <Typography
                      sx={{
                        textAlign: 'center',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        fontWeight: 'bold',
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    <Typography sx={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
                      {getFileSize(file.size)}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <Typography fontWeight="bold">Preview not available for:</Typography>
                    <Typography fontWeight="bold">{file.name}</Typography>
                  </Box>
                )
              ) : (
                <img
                  src={file.preview}
                  style={{ objectFit: 'contain', width: '100%', marginBottom: '1em' }}
                  alt="preview"
                  key={file.name}
                />
              );
            })}
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
                padding: 4,
                backgroundColor: FORTA_WHITE,
                borderRadius: '50%',
              }}
            >
              <img src={DocumentUploadIcon} alt="upload-icon" width={120} height={120} />
            </Box>
            <Typography variant="h4">Click to browse </Typography>
            <Typography>or drag document here</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ width: '100%' }}>
        <Box mt={2} sx={{ display: 'flex' }}>
          <FHInputField
            disabled
            fullWidth
            coloredBackground
            colorTheme={colorTheme}
            inputHeight={40}
            sx={{ flexGrow: 1, mr: '1em', boxShadow: 'none', border: '1px solid #E0E0E0' }}
            value={selectedFiles.map((file: any) => file.name).join(', ')}
            data-testid="visualInputField"
          />
          <FHButton colorTheme={colorTheme} onClick={open} data-testid={browseButtonTestId}>
            Browse
          </FHButton>
        </Box>
        <Box
          mt={2}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {errors.length > 0 && (
            <Box sx={{ flex: '1 1 auto' }}>
              {errors.map((error, index) => (
                <Typography key={index} fontWeight={'bold'} display={'block'} color={FORTA_ERROR}>
                  {error}
                </Typography>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              ml: 'auto',
            }}
          >
            <FHButton colorTheme={colorTheme} secondary onClick={handleClose} sx={{ mr: '1em' }}>
              Cancel
            </FHButton>
            <FHButton
              data-testid={uploadButtonTestId}
              colorTheme={colorTheme}
              onClick={handleSelect}
              disabled={Boolean(errors.length) || !selectedFiles.length}
            >
              {selectButtontext}
            </FHButton>
          </Box>
        </Box>
      </Box>
    </DragDropDialog>
  );
};

export default FHDropzone;
